/**
 * EBITDA Analysis - Board Level Executive Design
 * 2025 vs Budget 2025 (Primary) | vs 2024 (Secondary)
 * McKinsey/BCG Standard for Mining Sector
 */

import React, { useState } from 'react';
import { 
  ArrowLeft, TrendingUp, TrendingDown, Target, AlertTriangle,
  CheckCircle, ArrowRight, BarChart3, Activity, Gauge, ArrowUpRight, ArrowDownRight, Minus, FileText,
  DollarSign, Package, Users
} from 'lucide-react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell, ReferenceLine, Legend
} from 'recharts';
import { mockData } from '../../data/mockData';
import ExecutiveDrilldownCard from '../../components/ExecutiveDrilldownCard';
import ExecutiveSummarySection from '../../components/ExecutiveSummarySection';
import { 
  getCurrentPeriodLabel, 
  getPreviousPeriodLabel, 
  getBudgetLabel,
  getShortBudgetLabel,
  getTitleSuffix,
  getComparisonSubtitle
} from '../../utils/timeLabels';

// ============ PREMIUM DESIGN TOKENS ============
const colors = {
  bg: {
    page: '#F5F7FB',
    surface: '#FFFFFF',
    header: '#0D1538',
    muted: '#F8FAFC',
    surfaceAlt: '#F8FAFC',
  },
  border: {
    default: '#E3E8F1',
    soft: '#EEF2F7',
  },
  text: {
    primary: '#0E1633',
    secondary: '#425070',
    tertiary: '#7A859D',
    inverse: '#FFFFFF',
  },
  status: {
    positive: '#0C8A4B',
    positiveBg: '#EAF7EF',
    warning: '#A07A18',
    warningBg: '#F8F1DD',
    negative: '#C33D52',
    negativeBg: '#FBECEF',
  },
  chart: {
    primary: '#0D1538',
    budget: '#94A3B8',
    prior: '#CBD5E1',
    positive: '#0C8A4B',
    negative: '#C33D52',
    margin: '#B89A3C',
  },
  accent: {
    gold: '#B89A3C',
    goldBg: '#FBF8F0',
  }
};

const typography = {
  pageTitle: 'font-serif text-[32px] leading-[40px] font-semibold tracking-[-0.3px]',
  sectionTitle: 'font-serif text-[20px] leading-[26px] font-semibold tracking-[-0.2px]',
  cardLabel: 'font-sans text-[11px] leading-[14px] font-semibold tracking-[0.4px]',
  kpiValue: 'font-sans text-[26px] leading-[30px] font-semibold tracking-[-0.5px]',
  bodyText: 'font-sans text-[14px] leading-[22px] font-normal',
  smallText: 'font-sans text-[12px] leading-[16px] font-normal',
  microText: 'font-sans text-[10px] leading-[14px] font-medium tracking-[0.3px] uppercase',
};

// ============ HELPER FUNCTIONS ============
const formatM = (value) => {
  if (value === null || value === undefined) return '-';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return value;
  return num.toFixed(1);
};

const formatPct = (value) => {
  if (value === null || value === undefined) return '-';
  return value.toFixed(1);
};

const getVarianceColor = (variance, inverse = false) => {
  const isPositive = inverse ? variance < 0 : variance > 0;
  return isPositive ? colors.status.positive : colors.status.negative;
};

const getVarianceBg = (variance, inverse = false) => {
  const isPositive = inverse ? variance < 0 : variance > 0;
  return isPositive ? colors.status.positiveBg : colors.status.negativeBg;
};

// ============ COMPACT KPI TOOLTIP - MOVED OUTSIDE FOR PERFORMANCE ============
const CompactKPITooltip = ({ isVisible, title, definition, relevance, currentReading }) => {
  if (!isVisible) return null;
  return (
    <div 
      className="absolute z-50 w-56 bg-[#0A192F] border border-slate-700/50 rounded-lg shadow-xl p-3 bottom-full mb-2 left-1/2 -translate-x-1/2"
      style={{ boxShadow: '0 10px 25px rgba(10, 25, 47, 0.35)' }}
    >
      <div className="absolute left-1/2 -translate-x-1/2 w-2 h-2 bg-[#0A192F] border-slate-700/50 transform rotate-45 -bottom-1 border-b border-r" />
      <div className="space-y-2">
        <div className="flex items-center gap-1.5 pb-1.5 border-b border-slate-700/40">
          <div className="w-0.5 h-3 bg-gold-500 rounded-full"></div>
          <h4 className="text-gold-400 font-semibold text-xs uppercase tracking-wider">{title}</h4>
        </div>
        <div>
          <p className="text-slate-500 text-[10px] font-semibold uppercase tracking-wider mb-0.5">Definicao</p>
          <p className="text-white text-[11px] leading-snug">{definition}</p>
        </div>
        <div>
          <p className="text-slate-500 text-[10px] font-semibold uppercase tracking-wider mb-0.5">Relevancia</p>
          <p className="text-slate-300 text-[11px] leading-snug">{relevance}</p>
        </div>
        <div className="bg-slate-800/40 rounded px-2 py-1.5 border border-slate-700/20">
          <p className="text-slate-500 text-[10px] font-semibold uppercase tracking-wider mb-0.5">Leitura Actual</p>
          <p className="text-emerald-400 text-[11px] leading-snug">{currentReading}</p>
        </div>
      </div>
    </div>
  );
};

// ============ SEMANTIC COLOR SYSTEM FOR KPI CARDS ============
const statusColorsKPI = {
  positive: '#0C8A4B',
  positiveBg: '#EAF7EF',
  warning: '#A07A18',
  warningBg: '#F8F1DD',
  negative: '#C33D52',
  negativeBg: '#FBECEF',
};

const getSemanticBadgeStyleKPI = (val, inverse = false, useWarning = true) => {
  if (val === undefined || val === null) return { bg: statusColorsKPI.positiveBg, text: statusColorsKPI.positive };
  const isFavorable = inverse ? val <= 0 : val >= 0;
  const absVal = Math.abs(val);
  if (useWarning && !isFavorable && absVal <= 5) {
    return { bg: statusColorsKPI.warningBg, text: statusColorsKPI.warning };
  }
  if (isFavorable) {
    return { bg: statusColorsKPI.positiveBg, text: statusColorsKPI.positive };
  }
  return { bg: statusColorsKPI.negativeBg, text: statusColorsKPI.negative };
};

const getTrendIconInfoKPI = (vsBudget, vsPrior, inverse = false) => {
  const mainVal = vsBudget !== undefined ? vsBudget : vsPrior;
  if (mainVal === undefined) return null;
  const isFavorable = inverse ? mainVal <= 0 : mainVal >= 0;
  const isUp = mainVal > 0;
  return {
    isUp,
    bgColor: isFavorable ? statusColorsKPI.positiveBg : statusColorsKPI.negativeBg,
    iconColor: isFavorable ? statusColorsKPI.positive : statusColorsKPI.negative,
  };
};

// ============ KPI CARD COMPONENT - MOVED OUTSIDE FOR PERFORMANCE ============
const KPICard = ({ label, value, unit, vsBudget, vsPrior, changeUnit, inverse = false, highlight = false, tooltipData, useWarning = true, budgetLabel = 'vs Orç.', yoyLabel = 'YoY' }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  
  // Determine change unit: if KPI is in % and no explicit changeUnit, use p.p.
  const effectiveChangeUnit = changeUnit || (unit === '%' ? 'p.p.' : '%');
  const trendInfo = getTrendIconInfoKPI(vsBudget, vsPrior, inverse);
  const budgetStyle = getSemanticBadgeStyleKPI(vsBudget, inverse, useWarning);
  const priorStyle = getSemanticBadgeStyleKPI(vsPrior, inverse, useWarning);
  
  return (
    <div 
      className={`rounded-xl border p-5 transition-all duration-300 hover:shadow-lg hover:border-gold-300/50 overflow-visible relative ${highlight ? 'bg-slate-50 border-slate-300' : 'bg-white border-slate-200'}`}
      onMouseEnter={() => tooltipData && setShowTooltip(true)}
      onMouseLeave={() => tooltipData && setShowTooltip(false)}
    >
      {/* Compact Tooltip */}
      {tooltipData && (
        <CompactKPITooltip 
          isVisible={showTooltip}
          title={tooltipData.title}
          definition={tooltipData.definition}
          relevance={tooltipData.relevance}
          currentReading={tooltipData.currentReading}
        />
      )}
      
      {/* SCORECARD LAYOUT: Content on left, Trend Icon on right */}
      <div className="flex items-start justify-between">
        {/* Left side: Label, Value, Badges */}
        <div className="flex-1 min-w-0">
          {/* Label */}
          <p className="text-[11px] text-slate-500 tracking-wider uppercase mb-1.5">{label}</p>
          
          {/* Value + Unit */}
          <div className="flex items-baseline gap-1.5 mb-2">
            <span className="font-sans text-[28px] leading-[32px] font-semibold tracking-[-0.5px] text-primary">
              {value}
            </span>
            <span className="text-[13px] text-slate-400">{unit}</span>
          </div>
          
          {/* Comparison Badges - Same Line */}
          <div className="flex items-center gap-2 flex-wrap">
            {vsBudget !== undefined && (
              <span 
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap"
                style={{ backgroundColor: budgetStyle.bg, color: budgetStyle.text }}
              >
                {vsBudget >= 0 ? '↗' : '↘'} {vsBudget >= 0 ? '+' : ''}{vsBudget.toFixed(1)}{effectiveChangeUnit} {budgetLabel}
              </span>
            )}
            {vsPrior !== undefined && (
              <span 
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap"
                style={{ backgroundColor: priorStyle.bg, color: priorStyle.text }}
              >
                {vsPrior >= 0 ? '↗' : '↘'} {vsPrior >= 0 ? '+' : ''}{vsPrior.toFixed(1)}{effectiveChangeUnit} {yoyLabel}
              </span>
            )}
          </div>
        </div>
        
        {/* Right side: Trend Arrow Icon */}
        {trendInfo && (
          <div 
            className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ml-3"
            style={{ backgroundColor: trendInfo.bgColor }}
          >
            {trendInfo.isUp ? (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ color: trendInfo.iconColor }}>
                <path d="M8 3L14 9L12.6 10.4L9 6.8V13H7V6.8L3.4 10.4L2 9L8 3Z" fill="currentColor"/>
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ color: trendInfo.iconColor }}>
                <path d="M8 13L2 7L3.4 5.6L7 9.2V3H9V9.2L12.6 5.6L14 7L8 13Z" fill="currentColor"/>
              </svg>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const EBITDADetail = ({ year, language, setActiveSection, flowMap }) => {
  // ============ DATA SETUP ============
  const yearlyData = mockData.finance.yearly[year];
  const prevYearData = mockData.finance.yearly[year - 1] || mockData.finance.yearly[2024];
  const budgetData = mockData.finance.realVsBudget[year] || mockData.finance.realVsBudget[2025];
  
  // Dynamic time labels based on year (quarterly format for 2026)
  const currentPeriod = getCurrentPeriodLabel(year, language);
  const previousPeriod = getPreviousPeriodLabel(year, language);
  const budgetLabelFull = getBudgetLabel(year, language);
  const budgetLabelShort = getShortBudgetLabel(year, language);
  const titleSuffix = getTitleSuffix(year, language);
  const comparisonSubtitle = getComparisonSubtitle(year, language);
  const vsOrcLabel = year === 2026 ? 'vs Orç. 1T' : 'vs Orç.';
  const yoyLabel = year === 2026 ? `vs ${previousPeriod}` : 'YoY';
  
  // Calculate budget values
  const budgetEbitda = budgetData.ebitda?.budget || yearlyData.ebitda * 1.08;
  const budgetRevenue = budgetData.revenue?.budget || yearlyData.revenue * 1.03;
  const budgetOpex = budgetData.opex?.budget || yearlyData.opex * 0.98;
  const budgetEbitdaMargin = ((budgetEbitda / budgetRevenue) * 100).toFixed(1);
  
  // Variance calculations
  const ebitdaVsBudget = ((yearlyData.ebitda - budgetEbitda) / budgetEbitda * 100);
  const ebitdaVsPrior = ((yearlyData.ebitda - prevYearData.ebitda) / prevYearData.ebitda * 100);
  const marginVsBudget = yearlyData.ebitdaMargin - parseFloat(budgetEbitdaMargin);
  const marginVsPrior = yearlyData.ebitdaMargin - prevYearData.ebitdaMargin;
  const revenueVsBudget = ((yearlyData.revenue - budgetRevenue) / budgetRevenue * 100);
  const revenueVsPrior = ((yearlyData.revenue - prevYearData.revenue) / prevYearData.revenue * 100);
  const opexVsBudget = ((yearlyData.opex - budgetOpex) / budgetOpex * 100);
  const opexVsPrior = ((yearlyData.opex - prevYearData.opex) / prevYearData.opex * 100);
  
  // Monthly data with budget
  const monthlyData2024 = mockData.finance.monthly[year - 1] || [];
  const monthlyData2025 = mockData.finance.monthly[year] || [];
  
  const monthlyChartData = monthlyData2025.map((item, idx) => {
    const data2024 = monthlyData2024[idx] || {};
    const margin2025 = item.revenue > 0 ? ((item.ebitda / item.revenue) * 100) : 0;
    const margin2024 = data2024.revenue > 0 ? ((data2024.ebitda / data2024.revenue) * 100) : 0;
    const budgetEbitdaMonth = item.budget * 0.34; // Assuming 34% margin on budget
    const marginBudget = 34.0; // Budget margin target
    
    return {
      month: item.month,
      ebitda2025: item.ebitda || 0,
      budget2025: budgetEbitdaMonth,
      ebitda2024: data2024.ebitda || 0,
      margin2025: parseFloat(margin2025.toFixed(1)),
      margin2024: parseFloat(margin2024.toFixed(1)),
      marginBudget: marginBudget,
    };
  });

  // Income Statement Data with Budget
  const incomeStatementData = [
    { type: 'header', label: { pt: 'PROVEITOS OPERACIONAIS', en: 'OPERATING INCOME' } },
    { 
      label: { en: 'Sales', pt: 'Vendas' },
      actual: yearlyData.revenue * 0.92,
      budget: budgetRevenue * 0.92 * 0.95,
      prior: prevYearData.revenue * 0.91,
      type: 'row'
    },
    { 
      label: { en: 'Services', pt: 'Prestações de Serviços' },
      actual: yearlyData.revenue * 0.05,
      budget: budgetRevenue * 0.05 * 0.98,
      prior: prevYearData.revenue * 0.06,
      type: 'row'
    },
    { 
      label: { en: 'Other Income', pt: 'Outros Proveitos' },
      actual: yearlyData.revenue * 0.03,
      budget: budgetRevenue * 0.03 * 0.97,
      prior: prevYearData.revenue * 0.03,
      type: 'row'
    },
    { 
      label: { en: 'Total Income', pt: 'Total Proveitos' },
      actual: yearlyData.revenue,
      budget: budgetRevenue,
      prior: prevYearData.revenue,
      type: 'subtotal'
    },
    { type: 'header', label: { pt: 'CUSTOS OPERACIONAIS', en: 'OPERATING COSTS' } },
    { 
      label: { en: 'COGS', pt: 'CMVMC' },
      actual: -45.2,
      budget: -44.0,
      prior: -38.1,
      type: 'row',
      isNegative: true
    },
    { 
      label: { en: 'Personnel', pt: 'Custos com Pessoal' },
      actual: -28.5,
      budget: -27.5,
      prior: -26.2,
      type: 'row',
      isNegative: true
    },
    { 
      label: { en: 'Subcontracts', pt: 'Subcontratos' },
      actual: -12.8,
      budget: -13.5,
      prior: -10.5,
      type: 'row',
      isNegative: true
    },
    { 
      label: { en: 'General & Administrative', pt: 'Gerais e Administrativos' },
      actual: -15.8,
      budget: -16.2,
      prior: -14.1,
      type: 'row',
      isNegative: true
    },
    { 
      label: { en: 'Commercial Costs', pt: 'Custos de Comercialização' },
      actual: -4.2,
      budget: -4.5,
      prior: -3.8,
      type: 'row',
      isNegative: true
    },
    { 
      label: { en: 'Total Op. Costs', pt: 'Total Custos Op.' },
      actual: -(yearlyData.revenue - yearlyData.ebitda),
      budget: -(budgetRevenue - budgetEbitda),
      prior: -(prevYearData.revenue - prevYearData.ebitda),
      type: 'subtotal',
      isNegative: true
    },
    { 
      label: { en: 'EBITDA', pt: 'EBITDA' },
      actual: yearlyData.ebitda,
      budget: budgetEbitda,
      prior: prevYearData.ebitda,
      type: 'highlight'
    },
    { 
      label: { en: 'EBITDA Margin', pt: 'Margem EBITDA' },
      actual: yearlyData.ebitdaMargin,
      budget: parseFloat(budgetEbitdaMargin),
      prior: prevYearData.ebitdaMargin,
      type: 'margin',
      isPercent: true
    },
  ];

  // Determine headline status
  const ebitdaStatus = ebitdaVsBudget >= 0 ? 'above' : 'below';
  const marginStatus = marginVsBudget >= 0 ? 'expanded' : 'contracted';
  const mainPressure = opexVsBudget > 2 ? 'opex' : revenueVsBudget < -2 ? 'revenue' : 'none';

  // Executive Headline Messages
  const headlineMessages = {
    pt: {
      status: ebitdaStatus === 'above' 
        ? `EBITDA ${ebitdaVsPrior > 0 ? 'acima' : 'abaixo'} de ${previousPeriod} e ${Math.abs(ebitdaVsBudget).toFixed(0)}% acima do ${budgetLabelFull}`
        : `EBITDA ${ebitdaVsPrior > 0 ? 'acima' : 'abaixo'} de ${previousPeriod}, mas ${Math.abs(ebitdaVsBudget).toFixed(0)}% abaixo do ${budgetLabelFull}`,
      margin: marginStatus === 'expanded'
        ? `Expansão de margem de ${marginVsPrior.toFixed(1)}p.p. suportada por mix e alavancagem operacional`
        : `Contração de margem de ${Math.abs(marginVsPrior).toFixed(1)}p.p. devido a pressão de custos`,
      pressure: mainPressure === 'opex' 
        ? `Crescimento de Custos Op. ${opexVsBudget.toFixed(0)}% acima do plano limita conversão`
        : mainPressure === 'revenue'
        ? `Receita ${Math.abs(revenueVsBudget).toFixed(0)}% abaixo do plano reduz base de absorção`
        : `Execução operacional alinhada com expectativas orçamentais`
    },
    en: {
      status: ebitdaStatus === 'above'
        ? `EBITDA ${ebitdaVsPrior > 0 ? 'above' : 'below'} ${previousPeriod} and ${Math.abs(ebitdaVsBudget).toFixed(0)}% above ${budgetLabelFull}`
        : `EBITDA ${ebitdaVsPrior > 0 ? 'above' : 'below'} ${previousPeriod}, but ${Math.abs(ebitdaVsBudget).toFixed(0)}% below ${budgetLabelFull}`,
      margin: marginStatus === 'expanded'
        ? `Margin expansion of ${marginVsPrior.toFixed(1)}p.p. supported by mix and operating leverage`
        : `Margin contraction of ${Math.abs(marginVsPrior).toFixed(1)}p.p. due to cost pressure`,
      pressure: mainPressure === 'opex'
        ? `Op. Costs growth ${opexVsBudget.toFixed(0)}% above plan limits conversion`
        : mainPressure === 'revenue'
        ? `Revenue ${Math.abs(revenueVsBudget).toFixed(0)}% below plan reduces absorption base`
        : `Operational execution aligned with budget expectations`
    }
  };

  // Page data for PDF export
  const pageDataForExport = {
    scorecards: [
      { label: 'EBITDA', value: `${yearlyData.ebitda.toFixed(1)}M USD`, vsBudget: ebitdaVsBudget, vsPrior: ebitdaVsPrior },
      { label: language === 'pt' ? 'Margem EBITDA' : 'EBITDA Margin', value: `${yearlyData.ebitdaMargin.toFixed(1)}%`, vsBudget: marginVsBudget, vsPrior: marginVsPrior },
      { label: language === 'pt' ? 'Receita' : 'Revenue', value: `${yearlyData.revenue.toFixed(1)}M USD`, vsBudget: revenueVsBudget, vsPrior: revenueVsPrior },
      { label: 'OPEX', value: `${yearlyData.opex.toFixed(1)}M USD`, vsBudget: opexVsBudget }
    ],
    keyMessages: [
      headlineMessages[language].status,
      headlineMessages[language].margin,
      headlineMessages[language].pressure
    ]
  };

  // Custom Tooltip for Monthly Chart
  const MonthlyChartTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const ebitda2025 = payload.find(p => p.dataKey === 'ebitda2025')?.value || 0;
      const budget2025 = payload.find(p => p.dataKey === 'budget2025')?.value || 0;
      const ebitda2024 = payload.find(p => p.dataKey === 'ebitda2024')?.value || 0;
      const margin2025 = payload.find(p => p.dataKey === 'margin2025')?.value || 0;
      const marginBudget = payload.find(p => p.dataKey === 'marginBudget')?.value || 0;
      const vsBudget = budget2025 > 0 ? ((ebitda2025 - budget2025) / budget2025 * 100).toFixed(1) : 0;
      
      return (
        <div className="bg-[#0A192F] text-white rounded-lg px-4 py-3 shadow-xl border border-slate-700">
          <p className="font-semibold text-gold-400 mb-2">{label}</p>
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between gap-4">
              <span className="text-slate-400">{currentPeriod}:</span>
              <span className="font-semibold">{ebitda2025.toFixed(1)}M <span className="text-gold-400">({margin2025}%)</span></span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-slate-400">{budgetLabelShort}:</span>
              <span>{budget2025.toFixed(1)}M <span className="text-slate-400">({marginBudget}%)</span></span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-slate-400">{previousPeriod}:</span>
              <span className="text-slate-300">{ebitda2024.toFixed(1)}M</span>
            </div>
            <div className="border-t border-slate-600 pt-1.5 mt-1.5">
              <span className={`text-xs font-medium ${parseFloat(vsBudget) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {parseFloat(vsBudget) >= 0 ? '+' : ''}{vsBudget}% Orç.
              </span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-8" style={{ backgroundColor: colors.bg.page }} data-testid="ebitda-detail-section">
      {/* Header */}
      <div>
        <button 
          onClick={() => setActiveSection('income-statement')}
          className="flex items-center gap-2 text-sm mb-4 transition-colors hover:opacity-80"
          style={{ color: colors.text.tertiary }}
        >
          <ArrowLeft className="w-4 h-4" />
          {language === 'pt' ? 'Voltar à Demonstração de Resultados' : 'Back to Income Statement'}
        </button>
        <div className="flex items-start justify-between">
          <div>
            <h1 
              className="text-[38px] leading-[44px] font-semibold tracking-[-0.3px] mb-2"
              style={{ color: colors.text.primary, fontFamily: '"Source Serif 4", serif' }}
            >
              EBITDA | {titleSuffix}
            </h1>
            <p 
              className="text-[15px] leading-[22px]"
              style={{ color: colors.text.secondary }}
            >
              {comparisonSubtitle}
            </p>
          </div>
          
        </div>
        {/* Finance Flow Map - appears below title, above key messages */}
        {flowMap && <div className="mt-4">{flowMap}</div>}
      </div>

      {/* ============ KEY MESSAGES BAND - CAPEX Style ============ */}
      <div className="bg-gradient-to-r from-[#0A192F] to-[#1a2744] rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-1 h-5 bg-gold-500 rounded-full"></div>
          <h3 className="text-gold-400 font-semibold text-sm uppercase tracking-wider">
            {language === 'pt' ? 'Mensagens-Chave' : 'Key Messages'}
          </h3>
        </div>
        <div className="grid grid-cols-3 gap-5">
          {/* Performance */}
          <div className="bg-slate-700/30 border border-slate-600/30 rounded-xl p-4 transition-all duration-300 hover:scale-[1.02]">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-amber-500/30">
                <Target className="w-5 h-5 text-amber-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-sm mb-1.5 leading-tight text-gold-400">PERFORMANCE</h4>
                <p className="text-slate-400 text-xs leading-relaxed">{headlineMessages[language].status}</p>
              </div>
            </div>
          </div>
          
          {/* Margem */}
          <div className="bg-slate-700/30 border border-slate-600/30 rounded-xl p-4 transition-all duration-300 hover:scale-[1.02]">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-emerald-500/30">
                <CheckCircle className="w-5 h-5 text-emerald-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-sm mb-1.5 leading-tight text-emerald-400">
                  {language === 'pt' ? 'MARGEM' : 'MARGIN'}
                </h4>
                <p className="text-slate-400 text-xs leading-relaxed">{headlineMessages[language].margin}</p>
              </div>
            </div>
          </div>
          
          {/* Drivers */}
          <div className="bg-slate-700/30 border border-slate-600/30 rounded-xl p-4 transition-all duration-300 hover:scale-[1.02]">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-blue-500/30">
                <AlertTriangle className="w-5 h-5 text-blue-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-sm mb-1.5 leading-tight text-blue-400">
                  {language === 'pt' ? 'EXECUÇÃO' : 'EXECUTION'}
                </h4>
                <p className="text-slate-400 text-xs leading-relaxed">{headlineMessages[language].pressure}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ============ BLOCK 2: EXECUTIVE SCORECARD ============ */}
      <div className="grid grid-cols-5 gap-4 overflow-visible">
        <KPICard 
          label="EBITDA"
          value={formatM(yearlyData.ebitda)}
          unit="M USD"
          vsBudget={ebitdaVsBudget}
          vsPrior={ebitdaVsPrior}
          highlight={true}
          tooltipData={{
            title: 'EBITDA',
            definition: language === 'pt' 
              ? 'Resultado antes de juros, impostos, depreciação e amortização.'
              : 'Earnings before interest, taxes, depreciation and amortization.',
            relevance: language === 'pt'
              ? 'Principal indicador de geração de caixa operacional e eficiência da operação.'
              : 'Key indicator of operating cash generation and operational efficiency.',
            currentReading: language === 'pt'
              ? 'EBITDA de 102M USD (+9% vs orçamento, +31% YoY) reflete melhoria estrutural da rentabilidade operacional. O crescimento robusto posiciona a empresa para maior flexibilidade de investimento e desalavancagem.'
              : 'EBITDA of 102M USD (+9% budget, +31% YoY) reflects structural improvement in operating profitability. Robust growth positions company for greater investment flexibility and deleveraging.'
          }}
        />
        <KPICard 
          label={language === 'pt' ? 'Margem EBITDA' : 'EBITDA Margin'}
          value={yearlyData.ebitdaMargin}
          unit="%"
          vsBudget={marginVsBudget}
          vsPrior={marginVsPrior}
          tooltipData={{
            title: language === 'pt' ? 'Margem EBITDA' : 'EBITDA Margin',
            definition: language === 'pt'
              ? 'EBITDA dividido pela receita total.'
              : 'EBITDA divided by total revenue.',
            relevance: language === 'pt'
              ? 'Mede a eficiência operacional e capacidade de conversão de receita em lucro operacional.'
              : 'Measures operational efficiency and revenue-to-operating-profit conversion capability.',
            currentReading: language === 'pt'
              ? 'Margem de 34.2% representa expansão de +2.4p.p. YoY, demonstrando alavancagem operacional positiva. O mix favorável de pedras especiais e controlo de custos suportam a melhoria.'
              : 'Margin of 34.2% represents +2.4p.p. YoY expansion, demonstrating positive operating leverage. Favorable special stones mix and cost control support the improvement.'
          }}
        />
        <KPICard 
          label={language === 'pt' ? 'EBITDA/Quilates Vendidos (Pedras Regulares)' : 'EBITDA/Carats Sold (Regular Stones)'}
          value={Math.round(yearlyData.ebitda * 1000000 / 268000)}
          unit="USD"
          vsBudget={ebitdaVsBudget + 1.5}
          vsPrior={16}
          tooltipData={{
            title: language === 'pt' ? 'EBITDA/Quilate (Regulares)' : 'EBITDA/Carat (Regular)',
            definition: language === 'pt'
              ? 'EBITDA gerado por cada quilate vendido de pedras regulares.'
              : 'EBITDA generated per carat sold of regular stones.',
            relevance: language === 'pt'
              ? 'Métrica chave de rentabilidade unitária para o segmento de maior volume.'
              : 'Key unit profitability metric for the highest volume segment.',
            currentReading: language === 'pt'
              ? 'EBITDA de 380 USD/quilate (+16% YoY) para pedras regulares reflete eficiência operacional. Melhoria suportada por menor CMVMC e maior mix de qualidade.'
              : 'EBITDA of 380 USD/carat (+16% YoY) for regular stones reflects operational efficiency. Improvement supported by lower COGS and higher quality mix.'
          }}
        />
        <KPICard 
          label={language === 'pt' ? 'EBITDA/Quilates Vendidos (Pedras Especiais)' : 'EBITDA/Carats Sold (Special Stones)'}
          value={Math.round(yearlyData.ebitda * 1000000 / 36000)}
          unit="USD"
          vsBudget={ebitdaVsBudget + 3.2}
          vsPrior={22}
          tooltipData={{
            title: language === 'pt' ? 'EBITDA/Quilate (Especiais)' : 'EBITDA/Carat (Special)',
            definition: language === 'pt'
              ? 'EBITDA gerado por cada quilate vendido de pedras especiais.'
              : 'EBITDA generated per carat sold of special stones.',
            relevance: language === 'pt'
              ? 'Métrica de rentabilidade unitária para o segmento premium de maior valor.'
              : 'Unit profitability metric for the premium higher-value segment.',
            currentReading: language === 'pt'
              ? 'EBITDA de 2,833 USD/quilate (+22% YoY) para especiais confirma captura de valor premium. O preço médio elevado por quilate compensa custos de selecção e marketing premium.'
              : 'EBITDA of 2,833 USD/carat (+22% YoY) for specials confirms premium value capture. High average price per carat offsets selection and premium marketing costs.'
          }}
        />
        <KPICard 
          label="Net Debt/EBITDA"
          value={yearlyData.netDebtToEbitda.toFixed(2)}
          unit="x"
          vsBudget={-15}
          vsPrior={-((prevYearData.netDebtToEbitda - yearlyData.netDebtToEbitda) / prevYearData.netDebtToEbitda * 100)}
          inverse={true}
          changeUnit="%"
          tooltipData={{
            title: 'Net Debt/EBITDA',
            definition: language === 'pt'
              ? 'Rácio de alavancagem: dívida líquida sobre EBITDA anual.'
              : 'Leverage ratio: net debt over annual EBITDA.',
            relevance: language === 'pt'
              ? 'Valores mais baixos indicam menor pressão financeira e maior capacidade de endividamento.'
              : 'Lower values indicate less financial pressure and greater debt capacity.',
            currentReading: language === 'pt'
              ? 'Rácio de 0.35x está bem abaixo do limite de conforto de 2x, indicando estrutura de capital conservadora. A empresa tem ampla capacidade de endividamento para financiar crescimento.'
              : 'Ratio of 0.35x is well below the 2x comfort threshold, indicating conservative capital structure. Company has ample debt capacity to finance growth.'
          }}
        />
      </div>

      {/* ============ BLOCK 3: EXECUTIVE SUMMARY - CMVMC Style ============ */}
      <ExecutiveSummarySection
        title={language === 'pt' ? 'Sumário Executivo' : 'Executive Summary'}
        subtitle={language === 'pt' ? 'Análise consolidada do período' : 'Consolidated period analysis'}
        language={language}
        maxHeight="500px"
        dataTestId="ebitda-executive-summary"
      >
        {/* Executive Overview */}
        <div className="mb-6">
          <p className="text-slate-700 leading-relaxed text-sm mb-4">
            {language === 'pt' 
              ? `Em ${currentPeriod}, o EBITDA atingiu USD ${formatM(yearlyData.ebitda)}M, correspondendo a uma margem EBITDA de ${yearlyData.ebitdaMargin}% sobre os proveitos. Face a ${previousPeriod}, o EBITDA ficou ${ebitdaVsPrior >= 0 ? ebitdaVsPrior.toFixed(1) + '% acima' : Math.abs(ebitdaVsPrior).toFixed(1) + '% abaixo'}, enquanto face ao ${budgetLabelFull} o desvio foi de ${ebitdaVsBudget >= 0 ? '+' : ''}${ebitdaVsBudget.toFixed(1)}%. A leitura principal é que a empresa conseguiu preservar geração operacional de resultado num período em que a base de actividade foi mais exigente, mas em que a alavancagem económica do activo ainda não foi plenamente capturada.`
              : `In ${currentPeriod}, EBITDA reached USD ${formatM(yearlyData.ebitda)}M, corresponding to an EBITDA margin of ${yearlyData.ebitdaMargin}% on revenue. Compared to ${previousPeriod}, EBITDA was ${ebitdaVsPrior >= 0 ? ebitdaVsPrior.toFixed(1) + '% higher' : Math.abs(ebitdaVsPrior).toFixed(1) + '% lower'}, while vs ${budgetLabelFull} the deviation was ${ebitdaVsBudget >= 0 ? '+' : ''}${ebitdaVsBudget.toFixed(1)}%. The main reading is that the company managed to preserve operational result generation in a period where the activity base was more demanding, but where the economic leverage of assets was not yet fully captured.`}
          </p>
        </div>

        {/* Driver Sections - Drilldown Cards */}
        <div className="space-y-4">
          {/* Formação da margem EBITDA - Links to OPEX Detail */}
          <ExecutiveDrilldownCard
            icon={BarChart3}
            title={language === 'pt' ? 'Formação da Margem EBITDA' : 'EBITDA Margin Formation'}
            subtitle={language === 'pt' ? 'Estrutura de absorção de custos' : 'Cost absorption structure'}
            navigateTo="opex-detail"
            setActiveSection={setActiveSection}
            color="blue"
            language={language}
            tooltip={language === 'pt' 
              ? 'Esta análise mostra como os custos operacionais (CMVMC, Pessoal, FSE) são absorvidos pela receita para formar a margem EBITDA. É fundamental para entender a eficiência operacional da empresa e identificar oportunidades de melhoria na estrutura de custos.'
              : 'This analysis shows how operating costs (COGS, Personnel, G&A) are absorbed by revenue to form EBITDA margin. It is fundamental to understand the company\'s operational efficiency and identify improvement opportunities in the cost structure.'}
          >
            <p className="mb-3">
              {language === 'pt' 
                ? `A formação do EBITDA em ${currentPeriod} foi determinada sobretudo pela relação entre receita, CMVMC, custos com pessoal e gastos gerais e administrativos. Em termos executivos, o ponto central não é apenas o valor final do EBITDA, mas a forma como a operação absorveu estes blocos de custo e ainda assim reteve margem.`
                : `EBITDA formation in ${currentPeriod} was determined mainly by the relationship between revenue, COGS, personnel costs and G&A expenses. In executive terms, the central point is not just the final EBITDA value, but how the operation absorbed these cost blocks and still retained margin.`}
            </p>
            <div className="bg-white/60 rounded-lg p-3 border border-blue-100">
              <p className="text-xs font-semibold text-slate-700 mb-2">{language === 'pt' ? `Análise vs Orçamento e ${yoyLabel}` : `Analysis vs Budget and ${yoyLabel}`}</p>
              <ul className="text-xs text-slate-600 space-y-1">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                  {language === 'pt' ? `EBITDA ${vsOrcLabel}: ${ebitdaVsBudget >= 0 ? '+' : ''}${ebitdaVsBudget.toFixed(1)}%` : `EBITDA vs Budget: ${ebitdaVsBudget >= 0 ? '+' : ''}${ebitdaVsBudget.toFixed(1)}%`}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                  {language === 'pt' ? `EBITDA vs ${previousPeriod}: ${ebitdaVsPrior >= 0 ? '+' : ''}${ebitdaVsPrior.toFixed(1)}%` : `EBITDA vs ${previousPeriod}: ${ebitdaVsPrior >= 0 ? '+' : ''}${ebitdaVsPrior.toFixed(1)}%`}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                  {language === 'pt' ? `Margem EBITDA: ${yearlyData.ebitdaMargin}% (${marginVsBudget >= 0 ? '+' : ''}${marginVsBudget.toFixed(1)}p.p. ${vsOrcLabel})` : `EBITDA Margin: ${yearlyData.ebitdaMargin}% (${marginVsBudget >= 0 ? '+' : ''}${marginVsBudget.toFixed(1)}p.p. vs Budget)`}
                </li>
              </ul>
            </div>
          </ExecutiveDrilldownCard>

          {/* Receitas - Links to Revenue Detail */}
          <ExecutiveDrilldownCard
            icon={DollarSign}
            title={language === 'pt' ? 'Contribuição das Receitas' : 'Revenue Contribution'}
            subtitle={language === 'pt' ? 'Impacto da receita no EBITDA' : 'Revenue impact on EBITDA'}
            navigateTo="revenue-detail"
            setActiveSection={setActiveSection}
            color="emerald"
            language={language}
            tooltip={language === 'pt' 
              ? 'A receita é o principal driver do EBITDA. Quanto maior a receita gerada, maior a capacidade de absorver custos fixos e variáveis, preservando margem. Esta análise detalha a composição da receita por produto, cliente e período.'
              : 'Revenue is the main EBITDA driver. The higher the revenue generated, the greater the capacity to absorb fixed and variable costs, preserving margin. This analysis details revenue composition by product, customer and period.'}
          >
            <p className="mb-3">
              {language === 'pt' 
                ? `As receitas de ${year} atingiram USD ${formatM(yearlyData.revenue)}M, representando ${revenueVsPrior >= 0 ? 'um crescimento' : 'uma queda'} de ${Math.abs(revenueVsPrior).toFixed(1)}% face a ${year - 1}. Este resultado foi o principal driver do EBITDA, determinando a capacidade de absorção de custos fixos e variáveis.`
                : `${year} revenue reached USD ${formatM(yearlyData.revenue)}M, representing ${revenueVsPrior >= 0 ? 'growth' : 'a decline'} of ${Math.abs(revenueVsPrior).toFixed(1)}% vs ${year - 1}. This result was the main EBITDA driver, determining the capacity to absorb fixed and variable costs.`}
            </p>
          </ExecutiveDrilldownCard>

          {/* Custos Operacionais - Links to COGS */}
          <ExecutiveDrilldownCard
            icon={Package}
            title={language === 'pt' ? 'Pressão de Custos Directos' : 'Direct Cost Pressure'}
            subtitle={language === 'pt' ? 'CMVMC e custos de produção' : 'COGS and production costs'}
            navigateTo="opex-cogs"
            setActiveSection={setActiveSection}
            color="amber"
            language={language}
            tooltip={language === 'pt' 
              ? 'O CMVMC (Custo das Mercadorias Vendidas e Matérias Consumidas) é o maior bloco de custos variáveis e tem impacto directo na margem bruta. A eficiência na gestão de energia, consumíveis e manutenção determina quanto sobra para custos de estrutura e EBITDA.'
              : 'COGS (Cost of Goods Sold) is the largest block of variable costs with direct impact on gross margin. Efficiency in energy, consumables and maintenance management determines how much is left for structure costs and EBITDA.'}
          >
            <p className="mb-3">
              {language === 'pt' 
                ? `O CMVMC representou uma parte significativa da base de custos, determinando directamente a margem bruta disponível para absorver custos de estrutura. A eficiência na gestão do CMVMC foi determinante para a preservação da margem EBITDA.`
                : `COGS represented a significant portion of the cost base, directly determining the gross margin available to absorb structure costs. Efficiency in COGS management was determinant for EBITDA margin preservation.`}
            </p>
          </ExecutiveDrilldownCard>

          {/* Custos com Pessoal - Links to Personnel */}
          <ExecutiveDrilldownCard
            icon={Users}
            title={language === 'pt' ? 'Estrutura de Pessoal' : 'Personnel Structure'}
            subtitle={language === 'pt' ? 'Custos com pessoal e produtividade' : 'Personnel costs and productivity'}
            navigateTo="opex-personnel"
            setActiveSection={setActiveSection}
            color="purple"
            language={language}
            tooltip={language === 'pt' 
              ? 'Os custos com pessoal são um componente semi-fixo da estrutura de custos. A relação entre headcount, produtividade por colaborador e volume de actividade é crítica para entender a alavancagem operacional e o impacto no EBITDA quando a receita varia.'
              : 'Personnel costs are a semi-fixed component of the cost structure. The relationship between headcount, productivity per employee and activity volume is critical to understanding operational leverage and EBITDA impact when revenue varies.'}
          >
            <p className="mb-3">
              {language === 'pt' 
                ? `Os custos com pessoal são um componente estrutural relevante da base de custos. A análise da produtividade por colaborador e a relação com o volume de actividade são factores críticos para a leitura do EBITDA.`
                : `Personnel costs are a relevant structural component of the cost base. Productivity analysis per employee and the relationship with activity volume are critical factors for EBITDA reading.`}
            </p>
          </ExecutiveDrilldownCard>

          {/* Síntese à Gerência - Static card */}
          <div className="bg-gradient-to-r from-slate-100 to-slate-50 rounded-xl p-5 border border-slate-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-slate-200 flex items-center justify-center">
                <FileText className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Síntese à Gerência' : 'Management Synthesis'}</h4>
                <p className="text-xs text-slate-500">{language === 'pt' ? 'Leitura consolidada do período' : 'Consolidated reading of the period'}</p>
              </div>
            </div>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? `Em ${currentPeriod}, o EBITDA confirma que a empresa voltou a operar com geração operacional robusta, com USD ${formatM(yearlyData.ebitda)}M de EBITDA e margem de ${yearlyData.ebitdaMargin}%. Face a ${previousPeriod} e ao ${budgetLabelFull}, a leitura deve concentrar-se menos na variação nominal e mais na explicação económica do desvio: receita, absorção de custos directos, peso da estrutura e capacidade de retenção de margem.`
                : `In ${currentPeriod}, EBITDA confirms that the company has returned to operating with robust operational generation, with USD ${formatM(yearlyData.ebitda)}M EBITDA and ${yearlyData.ebitdaMargin}% margin. Compared to ${previousPeriod} and ${budgetLabelFull}, the reading should focus less on nominal variation and more on the economic explanation of the deviation: revenue, direct cost absorption, structure weight and margin retention capacity.`}
            </p>
            <p className="text-slate-600 text-xs leading-relaxed">
              {language === 'pt' 
                ? 'O principal sinal para a Gerência é que a base operacional já gera EBITDA com maior consistência; o passo seguinte será converter essa estabilidade em maior alavancagem de margem.'
                : 'The main signal for Management is that the operational base already generates EBITDA with greater consistency; the next step will be to convert this stability into greater margin leverage.'}
            </p>
          </div>
        </div>
      </ExecutiveSummarySection>

      {/* ============ BLOCK 4: MONTHLY EVOLUTION ============ */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="mb-4">
          <h3 className="font-serif text-lg font-semibold text-primary">
            {language === 'pt' ? 'Evolução Mensal EBITDA' : 'Monthly EBITDA Evolution'}
          </h3>
        </div>
        
        <ResponsiveContainer width="100%" height={320}>
          <ComposedChart data={monthlyChartData} margin={{ top: 20, right: 60, left: 20, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
            <XAxis 
              dataKey="month" 
              tick={{ fontSize: 11, fill: colors.text.secondary }} 
              stroke="transparent"
            />
            <YAxis 
              yAxisId="left"
              tick={{ fontSize: 11, fill: colors.text.tertiary }} 
              stroke="transparent"
              tickFormatter={(value) => `${value}M`}
              domain={[0, 'auto']}
            />
            <YAxis 
              yAxisId="right"
              orientation="right"
              tick={{ fontSize: 11, fill: colors.accent.gold }} 
              stroke="transparent"
              tickFormatter={(value) => `${value}%`}
              domain={[25, 45]}
            />
            <Tooltip content={<MonthlyChartTooltip />} />
            
            {/* Prior Year - Lighter */}
            <Bar 
              yAxisId="left"
              dataKey="ebitda2024" 
              fill={colors.chart.prior}
              radius={[2, 2, 0, 0]}
              barSize={16}
              name={`${year - 1}`}
            />
            {/* Budget - Solid color (blue-gray) */}
            <Bar 
              yAxisId="left"
              dataKey="budget2025" 
              fill="#64748B"
              radius={[2, 2, 0, 0]}
              barSize={16}
              name={language === 'pt' ? `Orçamento ${year}` : `Budget ${year}`}
            />
            {/* Actual - Primary */}
            <Bar 
              yAxisId="left"
              dataKey="ebitda2025" 
              fill={colors.chart.primary}
              radius={[2, 2, 0, 0]}
              barSize={16}
              name={`${year}`}
            />
            {/* Margin Line - 2024 */}
            <Line 
              yAxisId="right"
              type="monotone" 
              dataKey="margin2024" 
              stroke={colors.chart.prior}
              strokeWidth={2}
              strokeDasharray="4 2"
              dot={{ r: 3, fill: colors.chart.prior, stroke: '#fff', strokeWidth: 1 }}
              name={language === 'pt' ? `Margem ${year - 1}` : `Margin ${year - 1}`}
            />
            {/* Margin Line - Actual */}
            <Line 
              yAxisId="right"
              type="monotone" 
              dataKey="margin2025" 
              stroke={colors.chart.margin}
              strokeWidth={2}
              dot={{ r: 4, fill: colors.chart.margin, stroke: '#fff', strokeWidth: 2 }}
              name={language === 'pt' ? `Margem ${year}` : `Margin ${year}`}
            />
            {/* Margin Line - Budget */}
            <Line 
              yAxisId="right"
              type="monotone" 
              dataKey="marginBudget" 
              stroke="#64748B"
              strokeWidth={2}
              strokeDasharray="6 3"
              dot={false}
              name={language === 'pt' ? 'Margem Orç.' : 'Budget Margin'}
            />
          </ComposedChart>
        </ResponsiveContainer>
        
        {/* Legend at Bottom */}
        <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t border-slate-100 text-xs">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: colors.chart.primary }}></div>
            <span>{year}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: '#64748B' }}></div>
            <span>{language === 'pt' ? 'Orçamento' : 'Budget'} {year}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: colors.chart.prior }}></div>
            <span>{year - 1}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-0.5" style={{ backgroundColor: colors.chart.margin }}></div>
            <span>{language === 'pt' ? 'Margem' : 'Margin'} {year}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-0.5 border-b-2 border-dashed" style={{ borderColor: colors.chart.prior }}></div>
            <span>{language === 'pt' ? 'Margem' : 'Margin'} {year - 1}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-0.5 border-b-2 border-dashed" style={{ borderColor: '#64748B' }}></div>
            <span>{language === 'pt' ? 'Margem Orç.' : 'Budget Margin'}</span>
          </div>
        </div>
      </div>

      {/* ============ BLOCK 5: INCOME STATEMENT TO EBITDA ============ */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100">
          <h3 className="font-serif text-lg font-semibold text-primary">
            {language === 'pt' ? 'Demonstração de Resultados até EBITDA' : 'Income Statement to EBITDA'}
          </h3>
        </div>
        <div className="relative">
          {/* Fixed Header */}
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10">
              <tr style={{ backgroundColor: colors.bg.header }}>
                <th className="text-left py-3 px-4 text-xs font-semibold text-white uppercase tracking-wider w-[22%]">
                  {language === 'pt' ? 'Descrição' : 'Description'}
                </th>
                <th className="text-right py-3 px-3 text-xs font-semibold text-white uppercase tracking-wider w-[11%]">
                  {year}
                </th>
                <th className="text-right py-3 px-3 text-xs font-semibold text-white uppercase tracking-wider w-[11%]">
                  {language === 'pt' ? 'Orçamento' : 'Budget'} {year}
                </th>
                <th className="text-right py-3 px-3 text-xs font-semibold text-amber-300 uppercase tracking-wider w-[11%]">
                  {language === 'pt' ? 'Var. Abs.' : 'Abs. Var.'}
                </th>
                <th className="text-right py-3 px-3 text-xs font-semibold text-amber-300 uppercase tracking-wider w-[11%]">
                  {language === 'pt' ? 'Var. %' : 'Var. %'}
                </th>
                <th className="text-right py-3 px-3 text-xs font-semibold text-slate-400 uppercase tracking-wider w-[11%]">
                  {year - 1}
                </th>
                <th className="text-right py-3 px-3 text-xs font-semibold text-slate-400 uppercase tracking-wider w-[11%]">
                  {language === 'pt' ? 'Var. % YoY' : 'Var. % YoY'}
                </th>
              </tr>
            </thead>
          </table>
          
          {/* Scrollable Body */}
          <div className="max-h-[400px] overflow-y-auto">
            <table className="w-full text-sm">
              <tbody>
              {incomeStatementData.map((row, idx) => {
                // Handle header rows
                if (row.type === 'header') {
                  return (
                    <tr key={idx} className="bg-slate-100">
                      <td colSpan={7} className="py-2 px-4 text-xs font-bold text-slate-600 uppercase tracking-wider">
                        {row.label[language]}
                      </td>
                    </tr>
                  );
                }

                const absVarVsBudget = row.isPercent 
                  ? (row.actual - row.budget).toFixed(1)
                  : (row.actual - row.budget).toFixed(1);
                const pctVarVsBudget = row.budget !== 0 && !row.isPercent
                  ? ((row.actual - row.budget) / Math.abs(row.budget) * 100).toFixed(1)
                  : row.isPercent 
                    ? (row.actual - row.budget).toFixed(1)
                    : '0.0';
                const pctVarYoY = row.prior !== 0 && !row.isPercent
                  ? ((row.actual - row.prior) / Math.abs(row.prior) * 100).toFixed(1)
                  : row.isPercent 
                    ? (row.actual - row.prior).toFixed(1)
                    : '0.0';
                
                const isSubtotal = row.type === 'subtotal';
                const isHighlight = row.type === 'highlight';
                const isMargin = row.type === 'margin';
                
                // Color logic for variance (costs should be inverse)
                const budgetVarIsGood = row.isNegative 
                  ? parseFloat(pctVarVsBudget) <= 0 
                  : parseFloat(pctVarVsBudget) >= 0;
                const yoyVarIsGood = row.isNegative 
                  ? parseFloat(pctVarYoY) <= 0 
                  : parseFloat(pctVarYoY) >= 0;
                
                return (
                  <tr 
                    key={idx} 
                    className={`
                      border-b border-slate-100 
                      ${isSubtotal ? 'bg-slate-50 font-semibold' : ''} 
                      ${isHighlight ? 'bg-slate-100 font-bold' : ''} 
                      ${isMargin ? 'bg-white' : ''} 
                      hover:bg-slate-50/70 transition-colors
                    `}
                  >
                    <td className={`py-2.5 px-4 w-[22%] ${isMargin ? 'italic text-slate-500' : ''} ${row.type === 'row' ? 'pl-6' : ''}`}>
                      {row.label[language]}
                    </td>
                    <td className={`py-2.5 px-3 text-right tabular-nums w-[11%] ${isHighlight ? 'text-primary' : ''} ${isMargin ? 'italic text-slate-500' : ''}`}>
                      {row.isPercent ? `${row.actual.toFixed(1)}%` : formatM(row.actual)}
                    </td>
                    <td className={`py-2.5 px-3 text-right tabular-nums text-slate-500 w-[11%] ${isMargin ? 'italic' : ''}`}>
                      {row.isPercent ? `${row.budget.toFixed(1)}%` : formatM(row.budget)}
                    </td>
                    <td className={`py-2.5 px-3 text-right tabular-nums w-[11%] ${isMargin ? 'italic text-slate-400' : ''}`}>
                      {!isMargin ? (
                        <span style={{ color: budgetVarIsGood ? colors.status.positive : colors.status.negative }}>
                          {parseFloat(absVarVsBudget) > 0 ? '+' : ''}{absVarVsBudget}
                        </span>
                      ) : (
                        <span className="text-slate-400 italic">
                          {parseFloat(absVarVsBudget) > 0 ? '+' : ''}{absVarVsBudget} p.p.
                        </span>
                      )}
                    </td>
                    <td className="py-2.5 px-3 text-right w-[11%]">
                      {!isMargin ? (
                        <span 
                          className="inline-flex px-1.5 py-0.5 rounded text-xs font-semibold"
                          style={{ 
                            backgroundColor: budgetVarIsGood ? colors.status.positiveBg : colors.status.negativeBg,
                            color: budgetVarIsGood ? colors.status.positive : colors.status.negative
                          }}
                        >
                          {parseFloat(pctVarVsBudget) > 0 ? '+' : ''}{pctVarVsBudget}%
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400 italic">—</span>
                      )}
                    </td>
                    <td className={`py-2.5 px-3 text-right tabular-nums text-slate-400 w-[11%] ${isMargin ? 'italic' : ''}`}>
                      {row.isPercent ? `${row.prior.toFixed(1)}%` : formatM(row.prior)}
                    </td>
                    <td className="py-2.5 px-3 text-right w-[11%]">
                      {!isMargin ? (
                        <span 
                          className="text-xs font-medium"
                          style={{ color: yoyVarIsGood ? colors.status.positive : colors.status.negative }}
                        >
                          {parseFloat(pctVarYoY) > 0 ? '+' : ''}{pctVarYoY}%
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400 italic">
                          {parseFloat(pctVarYoY) > 0 ? '+' : ''}{pctVarYoY} p.p.
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EBITDADetail;
